/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { motion } from "framer-motion";
import React, { useMemo, useState } from "react";
import { GrFormClose } from "react-icons/gr";
import ReactTooltip from "react-tooltip";
import { customTooltip } from "../styles/general";
import StyledButton from "./StyledButton";

const Container = styled.div`
  width: 100%;
  position: relative;

  .input-and-button {
    display: flex;
    grid-gap: 15px;
  }
`;

const Input = styled.input<{ error?: boolean }>`
  width: 100%;
  height: 40px;
  border: 1px solid #dddddd;
  border-radius: 5px;
  outline: none;
  font-size: 15px;
  padding: 10px;
  color: #383838;

  margin-top: 5px;

  opacity: ${(props) => (props.disabled ? 0.5 : 1)};

  :focus {
    box-shadow: 0px 0px 6px #fb972e4d;
    border: 1px solid #fb972e;
  }

  &::placeholder {
    font-size: 14px;
    letter-spacing: 0.16px;
    color: #979797;
  }

  transition: box-shadow 0.3s, border 0.3s;

  ${(props) =>
    props.error &&
    css`
      border: 1px solid #c41700 !important;
      box-shadow: 0px 0px 6px #c417004d !important;
    `}

  background-color: #ffffff;
`;

const Item = styled(motion.div)`
  background-color: #fb972e19;
  display: inline-block;
  font-size: 14px;
  border: 1px solid #fb972e;
  border-radius: 15px;
  color: #fb972e;

  height: 30px;
  padding: 7px 12px;
  display: inline-flex;
  align-items: center;
  margin: 0 0.3rem 0.3rem 0;

  .delete-button {
    path {
      stroke: #fb972e;
    }
    margin-left: 5px;
    cursor: pointer;
    font-weight: bold;
    display: flex;
    align-items: center;
  }

  .email-tooltip {
    font-size: 14px;
    letter-spacing: 0px;
  }
`;

const Error = styled.p`
  position: absolute;
  margin: 0;
  font-size: 90%;
  color: #c41700;
`;

export enum BlockInputType {
  Email = "Email",
  Phone = "Phone",
  DeviceId = "DeviceId",
  DeviceIP = "DeviceIP",
  Domain = "Domain",
}

interface Props {
  placeholder: string;
  isLoading: boolean;
  inputType: BlockInputType;
  replaceAll?: (value: string) => string;
  isValid: (value: string) => string;
  handleClick: (items: string[]) => void;
}

export default function StyledBlockInput(props: Props): JSX.Element {
  const [value, setValue] = useState("");
  const [error, setError] = useState(null);
  const [items, setItems] = useState<string[]>([]);

  function handleClick() {
    const errorMessage = value ? props.isValid(value) : "";
    if (errorMessage) {
      setError(errorMessage);
      return;
    }

    props.handleClick([...items, ...(value ? [value.trim()] : [])]);
    setItems([]);
    setValue("");
  }

  function handleChange(evt: React.ChangeEvent<HTMLInputElement>): void {
    let value = props.replaceAll
      ? props.replaceAll(evt.target.value)
      : evt.target.value;
    setValue(value);
    setError(null);
  }

  function handleDelete(item: string) {
    setItems((items) => items.filter((i) => i !== item));
  }

  function handlePaste(evt: React.ClipboardEvent<HTMLInputElement>): void {
    evt.preventDefault();

    let paste = evt.clipboardData.getData("text");

    switch (props.inputType) {
      case BlockInputType.Email:
        let emails = paste.match(/[\w\d\.-]+@[\w\d\.-]+\.[\w\d\.-]+/g);

        if (emails) {
          let toBeAdded = emails.filter((email) => !isInList(email));
          setItems((items) => [...items, ...toBeAdded]);
          setValue("");
          setError(null);
        }
        break;

      case BlockInputType.DeviceIP:
        let value = props.replaceAll ? props.replaceAll(paste) : paste;
        setValue(value);
        break;

      case BlockInputType.Phone:
        let phoneValue = props.replaceAll ? props.replaceAll(paste) : paste;
        setValue(phoneValue);
        break;

      case BlockInputType.Domain:
        let domains: RegExpMatchArray | string[] = paste.match(
          /[\w\d\.-]+\.[\w\d\.-]+/g
        );
        domains = domains.map((domain) => domain.toLowerCase().trim());
        domains = Array.from(new Set(domains));
        domains = domains.filter(
          (domain) => !isInList(domain) && props.isValid(domain) === ""
        );

        setItems((items) => [...items, ...domains]);
        break;

      default:
        setValue(paste);
        break;
    }
  }

  function handleKeyDown(evt: React.KeyboardEvent<HTMLInputElement>): void {
    if (evt.key === "Enter") {
      handleClick();
      return;
    }
    if (["Tab", ",", " "].includes(evt.key)) {
      evt.preventDefault();

      if (isInList(value)) {
        setError(`${value} has already been added.`);
        return;
      }

      const errorMessage = props.isValid(value);

      if (errorMessage) {
        setError(errorMessage);
        return;
      }

      if (value) {
        setItems((items) => [...items, value.trim()]);
        setValue("");
      }
    }

    if (evt.key === "Backspace") {
      const isCurrentlyLastCharacterAPoint = value.slice(-1) === ".";
      const valueWithoutLastCharacter = value.slice(0, value.length - 1);

      if (isCurrentlyLastCharacterAPoint) setValue(valueWithoutLastCharacter);
    }
  }

  function isInList(email: string) {
    return items.includes(email);
  }

  const Items = useMemo(() => {
    return (
      <>
        {items.slice(0, 4).map((item) => (
          <Item key={item} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {item}
            <div className="delete-button" onClick={() => handleDelete(item)}>
              <GrFormClose />
            </div>
          </Item>
        ))}
        {items.length > 4 && (
          <Item data-tip data-for="tag" key="and-more">
            +{items.length - 4} more
            <ReactTooltip
              id="tag"
              place="bottom"
              effect="solid"
              backgroundColor="#333333"
              css={customTooltip}
            >
              {items.slice(4).map((item) => (
                <div className="email-tooltip">{item}</div>
              ))}
            </ReactTooltip>
          </Item>
        )}
      </>
    );
  }, [items]);

  return (
    <Container>
      {Items}
      <div className="input-and-button">
        <Input
          value={value}
          placeholder={props.placeholder}
          onKeyDown={handleKeyDown}
          onChange={handleChange}
          onPaste={handlePaste}
          error={Boolean(error)}
        />
        <StyledButton
          css={css`
            width: 112px;
            height: 40px;
            align-self: flex-end;
          `}
          disable={!Boolean(value) && !Boolean(items.length)}
          isLoading={props.isLoading}
          onClick={handleClick}
        >
          Block
        </StyledButton>
      </div>
      {error && <Error>{error}</Error>}
    </Container>
  );
}
